import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
// form
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useStore } from 'src/stores/store';
import { IContractInfo } from 'src/@types/Choqorak/MainTables/Contract/ContractInfo';
import CustomRHFAutocomplete from 'src/components/hook-form/CustomRHFAutocomplete';
import uuid from 'react-uuid';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';

import Label from 'src/components/Label';
import CalculateDuration from 'src/sections/common/CalculateDuration';
import { GetCurrencySymbol } from 'src/sections/common/GetCurrencySymbol';
import useThrottle from 'src/hooks/useThrottle';
// ----------------------------------------------------------------------

export default observer(function ContractInfoNewEditForm() {
  const { ContractInfoStore, ChoqorakCommonDropdown, ShopStore } = useStore();

  const [CustomerName, setCustomerName] = useState<string | undefined>('');
  const [ShopCode, setShopCode] = useState<string | undefined>('');
  const [shopCurrencyType, setShopCurrencyType] = useState<any>('');
  const [durationOfContract, setDurationOfContract] = useState<any>();
  const { translate } = useLocales();
  const {
    createContractInfo,
    updateContractInfo,
    editMode,

    selectedContractInfoDetail,
    clearSelectedContractInfo,
  } = ContractInfoStore;

  const {
    loadCustomerDDL,
    CustomerOption,
    loadShopDDL,
    ShopOption,
    loadShopContractTypeDDL,
    ShopContractTypeOption,
    loadFloorDDL,
    FloorOption,
    loadCurrencyTypeDDL,
  } = ChoqorakCommonDropdown;
  const { loadShopDetail, selectedShopDetail, clearSelectedShop } = ShopStore;
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const [checked, setChecked] = useState(
    editMode &&
      (selectedContractInfoDetail?.discountStartDate !== null ||
        selectedContractInfoDetail?.discountEndDate !== null)
      ? true
      : false
  );
  const NewContractInfoSchema = Yup.object().shape({
    customerName: Yup.string().required(`${translate('Validation.customerName')}`),
    shopCode: Yup.string().required(`${translate('Validation.shopCode')}`),
    // contractTypeId: Yup.string().required(`${translate('Validation.contractTypeId')}`),
    contractStartDate: Yup.string().required(`${translate('Validation.contractStartDate')}`),
    contractEndDate: Yup.string().required(`${translate('Validation.contractEndDate')}`),
  });

  const defaultValues = useMemo<IContractInfo>(
    () => ({
      contractId: selectedContractInfoDetail?.contractId,
      customerId: selectedContractInfoDetail?.customerId || undefined,
      floorId: selectedContractInfoDetail?.floorId || undefined,
      shopId: selectedContractInfoDetail?.shopId || undefined,
      contractStartDate: selectedContractInfoDetail?.contractStartDate || undefined,
      contractEndDate: selectedContractInfoDetail?.contractEndDate || undefined,
      discountStartDate: selectedContractInfoDetail?.discountStartDate || undefined,
      discountEndDate: selectedContractInfoDetail?.discountEndDate || undefined,
      initialRentAmount: selectedContractInfoDetail?.initialRentAmount || undefined,
      finalRentPerMonth: selectedContractInfoDetail?.finalRentPerMonth || undefined,
      advancePayment: selectedContractInfoDetail?.advancePayment || undefined,
      contractTypeId: selectedContractInfoDetail?.contractTypeId || undefined,
    }),
    [selectedContractInfoDetail]
  );

  const methods = useForm<IContractInfo>({
    resolver: yupResolver(NewContractInfoSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    watch,
    setValue,
    control,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const val = watch();
  const toUTCDateOnly = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()));
  };

  const onSubmit = async (data: IContractInfo) => {
    try {
      data.contractStartDate = toUTCDateOnly(val.contractStartDate!);
      data.contractEndDate = toUTCDateOnly(val.contractEndDate!);
      data.contractTypeId = 1;
      console.log(data)

      if (data.contractId === undefined) {
        // --- create ---
        await createContractInfo(data);
        reset();
        enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
        clearSelectedShop();
        navigate(PATH_DASHBOARD.ContractInfo.list);
      } else {
        // --- update ---
        await updateContractInfo(data);
        reset();
        enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
        clearSelectedShop();
        navigate(PATH_DASHBOARD.ContractInfo.list);
      }
    } catch (err: any) {
      try {
        const json = JSON.parse(err?.request?.response);
        if (json.error.AdvancePayment != null) {
          setError('afterSubmit', { ...err, message: json.error.AdvancePayment });
        } else if (json.error.contractEndDate != null) {
          setError('afterSubmit', { ...err, message: json.error.contractEndDate });
        } else if (json.error.contractStartDate != null) {
          setError('afterSubmit', { ...err, message: json.error.contractStartDate });
        } else if (json.error.contractTypeId != null) {
          setError('afterSubmit', { ...err, message: json.error.contractTypeId });
        } else if (json.error.customerId != null) {
          setError('afterSubmit', { ...err, message: json.error.customerId });
        } else if (json.error.finalRentPerMonth != null) {
          setError('afterSubmit', { ...err, message: json.error.finalRentPerMonth });
        } else if (json.error.shopId != null) {
          setError('afterSubmit', { ...err, message: json.error.shopId });
        } else {
          setError('afterSubmit', { ...err, message: json.message });
        }
      } catch {
        // fallback if response is not JSON
        setError('afterSubmit', { ...err, message: err.message || 'Unexpected error' });
      }
    }
  };


  useEffect(() => {
    if (editMode) {
      reset(defaultValues);
    }
    if (!editMode) {
      reset(defaultValues);
    }
  }, [reset, editMode, defaultValues]);

  //#region for autocomplete

  const customerName = watch('customerName');

  const handleAutocompleteChange =
    (options: any[], valueKey: keyof IContractInfo, idKey: keyof IContractInfo) =>
      (_event: any, newValue: any) => {
        const selectedOption = options.find((item) => item.text === newValue);
        if (selectedOption) {
          setValue(idKey, Number(selectedOption.value));
          setValue(valueKey, selectedOption.text);
        } else {
          setValue(idKey, undefined);
          setValue(valueKey, '');
        }
      };
  useEffect(() => {
    loadCustomerDDL();
    loadFloorDDL();
    if (val.floorId) {
      loadShopDDL(val.floorId, !editMode ? false : true);
    }
    loadCurrencyTypeDDL();
    loadShopContractTypeDDL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [val.floorId]);
  //#endregion

  useEffect(() => {
    if (val.shopId) {
      loadShopDetail(val.shopId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [val.shopId]);

  useEffect(() => {
    // Set the initial rent amount when selectedShopDetail changes
    if (selectedShopDetail?.rentPerMonth) {
      setValue('initialRentAmount', selectedShopDetail.rentPerMonth);
      setValue('finalRentPerMonth', selectedShopDetail.rentPerMonth);

      if (!editMode) {
        setValue('advancePayment', selectedShopDetail.rentPerMonth);
      }
      if (editMode) {
        setValue('advancePayment', selectedContractInfoDetail?.advancePayment);
      }
    }
  }, [selectedShopDetail, setValue, editMode, selectedContractInfoDetail]);

  useEffect(() => {
    if (editMode) {
      if (defaultValues.customerId) {
        loadCustomerDDL().then((res) => {
          setCustomerName(CustomerOption.find((e) => e.value === defaultValues.customerId)?.text);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CustomerName]);

  useEffect(() => {
    setValue('customerName', CustomerName);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CustomerName]);

  useEffect(() => {
    if (editMode) {
      if (defaultValues.floorId) {
        loadShopDDL(val.floorId!, !editMode ? false : true).then((res) => {
          setShopCode(ShopOption.find((e) => e.value === defaultValues.shopId)?.text);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ShopCode]);

  useEffect(() => {
    setValue('shopCode', ShopCode);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ShopCode]);

  const contractStartDate = watch('contractStartDate');
  const contractEndDate = watch('contractEndDate');
  const contractDisCountStartDate = watch('discountStartDate');
  const contractDisCountEndDate = watch('discountEndDate');

  useEffect(() => {
    setValue(
      'contractDuration',
      CalculateDuration(translate, contractStartDate, contractEndDate, true)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractStartDate, contractEndDate]);

  const setCurrencyType = (shopCode?: string) => {
    const selectedShop = ShopOption.find((option) => option.text === shopCode);

    if (selectedShop && selectedShop.rentCarrency) {
      setShopCurrencyType(selectedShop.rentCarrencyId); // فقط زمانی که ارز وجود داشته باشد
    }
  };
  useEffect(() => {
    if (editMode) {
      setCurrencyType(selectedContractInfoDetail?.shopCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  interface DurationType {
    type: number;
    value: number;
  }
  const Duration: DurationType[] = [
    { type: 1, value: 1 },
    { type: 2, value: 2 },
    { type: 3, value: 3 },
    { type: 4, value: 4 },
    { type: 5, value: 5 },
    { type: 6, value: 6 },
    { type: 7, value: 7 },
    { type: 8, value: 8 },
    { type: 9, value: 9 },
    { type: 10, value: 10 },
    { type: 11, value: 11 },
    { type: 12, value: 12 },
    { type: 13, value: 13 },
    { type: 14, value: 14 },
    { type: 15, value: 15 },
  ];



  useEffect(() => {
    if (durationOfContract !== undefined) {
      const startDate = typeof val.contractStartDate === 'string' ? new Date(val.contractStartDate) : val.contractStartDate;
      setValue(
        'contractEndDate',
        new Date(
          new Date(startDate!).setFullYear(
            new Date(startDate!).getFullYear() + Number(durationOfContract)
          )
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationOfContract]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };


  const getUserDataFromLocalStorage: any = localStorage.getItem('userData');

  const changeLocalStorgeStringToValue = JSON.parse(getUserDataFromLocalStorage);

  const is_super_admin = changeLocalStorgeStringToValue.IsSuperAdmin;

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Label sx={{ p: 3, fontSize: 18, color: 'white' }} color="warning">
            <strong>{translate('contractsInfo.ContractDuration')}: </strong>{' '}
            {CalculateDuration(translate, contractStartDate, contractEndDate)}
          </Label>
          <Label sx={{ p: 3, fontSize: 18, color: 'white' }} color="warning">
            <strong>{translate('contractsInfo.DurationOfDiscount')}: </strong>{' '}
            {CalculateDuration(translate, contractDisCountStartDate, contractDisCountEndDate)}
          </Label>

          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(3, 1fr)' },
              }}
            >
              <CustomRHFAutocomplete
                name="customerName"
                label={translate('contractsInfo.customerName')}
                showAsterisk
                placeholder={translate('contractsInfo.customerName')}
                value={customerName || ''}
                options={CustomerOption.map((option) => option.text)}
                getOptionLabel={(option) => option}
                onChange={handleAutocompleteChange(CustomerOption, 'customerName', 'customerId')}
                fullWidth
                renderOption={(props, option) => (
                  <li {...props} key={`${option}-${uuid()}`}>
                    {option}
                  </li>
                )}
              />

              <RHFSelect
                name="floorId"
                label={translate('Floor.Floor')}
                size="medium"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                sx={{ maxWidth: { md: '100%' } }}
                onChange={(e) => {
                  // Update your state
                  setValue('floorId', Number(e.target.value)); // Update the form value
                }}
              >
                {FloorOption.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 0.75,
                      typography: 'body2',
                      textTransform: 'capitalize',
                    }}
                  >
                    {option.text}
                  </MenuItem>
                ))}
              </RHFSelect>

              <CustomRHFAutocomplete
                name="shopCode"
                label={translate('Shop.ShopCode')}
                showAsterisk
                placeholder={translate('Shop.ShopCode')}
                value={watch('shopCode') || ''}
                options={ShopOption.map((option) => option.text)}
                getOptionLabel={(option) => option}
                onChange={(event, newValue) => {
                  handleAutocompleteChange(ShopOption, 'shopCode', 'shopId')(event, newValue);
                  if (typeof newValue === 'string') {
                    setCurrencyType(newValue); // اطمینان از اینکه newValue از نوع string است
                  }
                }}
                fullWidth
                renderOption={(props, option) => (
                  <li {...props} key={`${option}-${uuid()}`}>
                    {option}
                  </li>
                )}
              />

              <LocalizDatePicker
                name="contractStartDate"
                label={translate('contractsInfo.StartDate')}
                control={control}
                showAsterisk={false}
                isDisableFuture={is_super_admin?false:true}
              />
              {!editMode ? (
                <RHFSelect
                  name="duration"
                  onChange={(e) => {
                    setValue('duration', Number(e.target.value));
                    setDurationOfContract(e.target.value);
                  }}
                  value={watch('duration')}
                  label={translate('contractsInfo.SetContractDuration')}
                >
                  <option value="" />
                  {Duration.map((op, index) => (
                    <option key={index} value={op.type}>
                      {op.value}
                    </option>
                  ))}
                </RHFSelect>
              ) : (
                ''
              )}
              <LocalizDatePicker
                name="contractEndDate"
                label={translate('contractsInfo.EndDate')}
                control={control}
                showAsterisk={false}
              />
              {/* <RHFSelect
                name="contractTypeId"
                label={translate('ContractType.ContractType')}
                size="medium"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                sx={{ maxWidth: { md: '100%' } }}
                onChange={(e) => {
                 
                  setValue('contractTypeId', Number(e.target.value)); // Update the form value
                }}
              >
                {ShopContractTypeOption.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 0.75,
                      typography: 'body2',
                      textTransform: 'capitalize',
                    }}
                  >
                    {option.text}
                  </MenuItem>
                ))}
              </RHFSelect> */}
              <RHFTextField
                name="contractDuration"
                label={translate('contractsInfo.ContractDuration')}
                showAsterisk={true}
                sx={{ direction: 'rtl' }}
                InputProps={{
                  readOnly: true,
                }}
              />
              <Controller
                name="initialRentAmount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={
                      <>
                        {translate('contractsInfo.InitialRentAmount')}
                        <span style={{ color: 'red', marginLeft: 4 }}>*</span>{' '}
                        {/* Asterisk for required field */}
                      </>
                    }
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          {' '}
                          {shopCurrencyType === 2
                            ? GetCurrencySymbol('afn')
                            : GetCurrencySymbol('dollar')}
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                  />
                )}
              />

              <RHFTextField
                name="finalRentPerMonth"
                label={translate('contractsInfo.FinalRentPerMonth')}
                showAsterisk={true}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      {' '}
                      {shopCurrencyType === 2
                        ? GetCurrencySymbol('afn')
                        : GetCurrencySymbol('dollar')}
                    </InputAdornment>
                  ),
                }}
              />
              <RHFTextField
                name="advancePayment"
                label={translate('contractsInfo.AdvancePayment')}
                showAsterisk={true}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {' '}
                      {shopCurrencyType === 2
                        ? GetCurrencySymbol('afn')
                        : GetCurrencySymbol('dollar')}
                    </InputAdornment>
                  ),
                }}
              />
              {!checked ? (
                <Controller
                  name="hasDiscount"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          onChange={(e) => {
                            field.onChange(e); // Notify React Hook Form of the value change
                            handleChange(e); // Handle the switch state change
                          }}
                          checked={field.value}
                        />
                      }
                      label={translate('HasDiscount')}
                      labelPlacement="end"
                    />
                  )}
                />
              ) : (
                ''
              )}
              {checked ? (
                <>
                  <LocalizDatePicker
                    name="discountStartDate"
                    label={translate('contractsInfo.DiscountStartDate')}
                    control={control}
                    showAsterisk={false}
                  />

                  <LocalizDatePicker
                    name="discountEndDate"
                    label={translate('contractsInfo.DiscountEndDate')}
                    control={control}
                    showAsterisk={false}
                  />
                </>
              ) : (
                ''
              )}
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={
                  !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                }
              >
                {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  clearSelectedContractInfo();
                  clearSelectedShop();
                  reset();
                  navigate(PATH_DASHBOARD.ContractInfo.list);
                }}
              >
                {translate('CRUD.BackToList')}
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
